import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Table, Button, Form, Tabs, Tab, Badge, Modal } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaTags, FaCoffee, FaListAlt, FaLayerGroup } from "react-icons/fa";
import { 
  getAllProductsApi, createProductApi, updateProductApi, deleteProductApi,
  getAllProductCategoriesApi, createProductCategoryApi, updateProductCategoryApi, deleteProductCategoryApi,
  getBomApi,
  getAllModifierGroupsApi, createModifierGroupApi, updateModifierGroupApi, deleteModifierGroupApi,
  getModifiersByGroupApi, createModifierApi, updateModifierApi, deleteModifierApi,
  getProductModifierGroupsApi,
  getModifierBomApi, updateModifierBomApi, updateBranchProductApi
} from "./service/api_service";
import { getAllInventoryApi } from "../inventory/service/api_service";

// Child components
import { ItemModal } from './components/ItemModal';
import { CategoryModal } from './components/CategoryModal';
import { ModGroupModal } from './components/ModGroupModal';
import { ModItemsModal } from './components/ModItemsModal';
import { ModBomModal } from './components/ModBomModal';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import { GlobalAlert } from '../../global/components/GlobalAlert';
import { useAuth } from "../authen/AuthContext";
import ImageViewerButton from "../layout/ImageViewerButton";

export default function Menu() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("items");
  
  // --- States for Menu Items (Products) ---
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemFormData, setItemFormData] = useState({ id: null, name: "", base_price: 0, category_id: "", is_active: true, image_url: "" });
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState("");

  const [showBranchModal, setShowBranchModal] = useState(false);
  const [branchFormData, setBranchFormData] = useState({ id: null, name: "", is_available: true, custom_price: 0 });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchCatQuery, setSearchCatQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const [categories, setCategories] = useState([]);
  const [, setLoadingCategories] = useState(true);
  const [showCatModal, setShowCatModal] = useState(false);
  const [catFormData, setCatFormData] = useState({ id: null, name: "" });

  const [bomItems, setBomItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  // --- Shared Modifiers ---
  const [sharedModGroups, setSharedModGroups] = useState([]);
  const [loadingSharedModGroups, setLoadingSharedModGroups] = useState(true);
  const [showModGroupModal, setShowModGroupModal] = useState(false);
  const [modGroupFormData, setModGroupFormData] = useState({ id: null, name: "", is_required: false, max_choices: 1, product_id: null, is_active: true });

  // --- Specific Modifiers & Mappings ---
  const [productModGroups, setProductModGroups] = useState([]); // Array of specific groups { id, name, is_required, max_choices, choices: [] }
  const [selectedSharedGroups, setSelectedSharedGroups] = useState([]);

  // --- Choices Modal (Shared & Specific) ---
  const [showModItemsModal, setShowModItemsModal] = useState(false);
  const [currentModGroup, setCurrentModGroup] = useState(null); // The group object being edited
  const [currentModGroupIndex, setCurrentModGroupIndex] = useState(null); // For Add Mode specific groups
  const [modItems, setModItems] = useState([]); 
  const [, setLoadingModItems] = useState(false);

  // --- Modifier BOM ---
  const [showModBomModal, setShowModBomModal] = useState(false);
  const [currentModifierForBom, setCurrentModifierForBom] = useState(null);
  const [modBomItems, setModBomItems] = useState([]);

  const [error, setError] = useState("");
  const [modalMode, setModalMode] = useState("add"); 
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: "", id: null, name: "", parentId: null, index: null });

  const token = localStorage.getItem("token");
  const canEdit = currentUser?.role === "master" || currentUser?.role === "admin";

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const res = await getAllProductsApi(token);
      setProducts(res.data);
    } catch (err) {
      setError("Failed to fetch menu items");
    } finally {
      setLoadingProducts(false);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const res = await getAllProductCategoriesApi(token);
      setCategories(res.data);
    } catch (err) {
      setError("Failed to fetch menu categories");
    } finally {
      setLoadingCategories(false);
    }
  }, [token]);

  const fetchInventoryItems = useCallback(async () => {
    try {
      const res = await getAllInventoryApi(token);
      setInventoryItems(res.data);
    } catch (err) {}
  }, [token]);

  const fetchSharedModifierGroups = useCallback(async () => {
    try {
      setLoadingSharedModGroups(true);
      const res = await getAllModifierGroupsApi(token); 
      setSharedModGroups(res.data);
    } catch (err) {} finally {
      setLoadingSharedModGroups(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchInventoryItems();
    fetchSharedModifierGroups();
  }, [fetchProducts, fetchCategories, fetchInventoryItems, fetchSharedModifierGroups]);

  const filteredProducts = products.filter(product => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = product.name.toLowerCase().includes(searchLower) || 
                          (product.category_name && product.category_name.toLowerCase().includes(searchLower));
    const matchesCategory = filterCategory ? String(product.category_id) === String(filterCategory) : true;
    return matchesSearch && matchesCategory;
  });

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchCatQuery.toLowerCase())
  );

  // --- Product Handlers ---
  const handleShowAddItem = () => {
    setModalMode("add");
    setItemFormData({ id: null, name: "", base_price: 0, category_id: categories.length ? categories[0].id : "", is_active: true, image_url: "" });
    setProductImageFile(null);
    setProductImagePreview("");
    setProductModGroups([]);
    setSelectedSharedGroups([]);
    setShowItemModal(true);
    setError("");
  };

  const loadProductMappingAndSpecifics = async (productId) => {
    try {
      const res = await getProductModifierGroupsApi(token, productId);
      const specific = res.data.filter(g => g.product_id === productId);
      const sharedMapped = res.data.filter(g => g.product_id === null).map(g => g.id);
      setProductModGroups(specific);
      setSelectedSharedGroups(sharedMapped);

      // Fetch Product BOM
      const bomRes = await getBomApi(token, productId);
      setBomItems(bomRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleShowEditItem = async (product) => {
    setModalMode("edit");
    setItemFormData({ 
      id: product.id, 
      name: product.name, 
      base_price: product.base_price, // Use actual global base_price for global edit
      category_id: product.category_id || "",
      is_active: product.is_active,
      image_url: product.image_url || ""
    });
    setProductImageFile(null);
    setProductImagePreview(product.image_url ? `${process.env.REACT_APP_API_URL}${product.image_url}` : "");
    await loadProductMappingAndSpecifics(product.id);
    setShowItemModal(true);
    setError("");
  };

  const handleShowBranchSettings = (product) => {
    setBranchFormData({
      id: product.id,
      name: product.name,
      is_available: product.is_active === 1 || product.is_active === true,
      custom_price: product.price
    });
    setShowBranchModal(true);
    setError("");
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateBranchProductApi(token, branchFormData.id, {
        is_available: branchFormData.is_available,
        custom_price: branchFormData.custom_price || null
      });
      setShowBranchModal(false);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update branch settings");
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!itemFormData.name || itemFormData.name.trim() === "") return setError("Menu Name is required.");
    if (itemFormData.base_price === "" || Number(itemFormData.base_price) < 0) return setError("Base Price must be 0 or greater.");
    for (const group of productModGroups) {
      if (!group.name || group.name.trim() === "") return setError("All Specific Add-on Group Names are required.");
      if (group.choices) {
        for (const choice of group.choices) {
          if (!choice.name || choice.name.trim() === "") return setError("All Specific Add-on Choice Names are required.");
          if (choice.price_adjust === "" || Number(choice.price_adjust) < 0) return setError("Price Adjust (+THB) cannot be negative.");
        }
      }
    }

    try {
      const payload = { 
        ...itemFormData, 
        category_id: itemFormData.category_id ? Number(itemFormData.category_id) : null,
        shared_modifier_group_ids: selectedSharedGroups,
        specific_modifier_groups: productModGroups,
        bomItems: bomItems.filter(item => item.inventory_item_id && item.quantity > 0)
          .map(item => ({ inventory_item_id: Number(item.inventory_item_id), quantity: Number(item.quantity) }))
      };

      const formData = new FormData();
      formData.append("productData", JSON.stringify(payload));
      if (productImageFile) {
        formData.append("image", productImageFile);
      }

      if (modalMode === "add") {
        await createProductApi(token, formData);
      } else {
        await updateProductApi(token, payload.id, formData);
      }
      setShowItemModal(false);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };



  // --- Choices Setup (Inline Table) ---
  const handleManageChoices = async (group, index = null) => {
    setCurrentModGroup(group);
    setCurrentModGroupIndex(index);
    setShowModItemsModal(true);
    
    if (group.id) {
      setLoadingModItems(true);
      try {
        const res = await getModifiersByGroupApi(token, group.id);
        setModItems(res.data);
      } catch (err) {
        setError("Failed to fetch choices");
      } finally {
        setLoadingModItems(false);
      }
    } else {
      // Add mode: load from local state
      setModItems(group.choices || []);
    }
  };

  const handleAddChoiceRow = async () => {
    const newChoice = { id: null, name: "New Choice", price_adjust: 0, is_default: false, is_active: true };
    if (currentModGroup?.id) { // Edit mode (Group exists in DB)
      try {
        await createModifierApi(token, currentModGroup.id, newChoice);
        const res = await getModifiersByGroupApi(token, currentModGroup.id);
        setModItems(res.data);
      } catch (err) {
        console.error(err);
      }
    } else { // Add mode (Group only in local state)
      const updatedChoices = [...modItems, newChoice];
      setModItems(updatedChoices);
      // Sync to parent productModGroups
      const updatedGroups = [...productModGroups];
      updatedGroups[currentModGroupIndex].choices = updatedChoices;
      setProductModGroups(updatedGroups);
    }
  };

  const handleModItemChange = (index, field, value) => {
    const updated = [...modItems];
    updated[index][field] = value;
    setModItems(updated);
    
    // If in Add Mode, sync to parent state immediately
    if (!currentModGroup?.id) {
      const updatedGroups = [...productModGroups];
      updatedGroups[currentModGroupIndex].choices = updated;
      setProductModGroups(updatedGroups);
    }
  };

  const handleAutoSaveChoice = async (item) => {
    if (currentModGroup?.id && item.id) {
      try {
        await updateModifierApi(token, item.id, item);
      } catch (err) {
        console.error(err);
      }
    }
  };

  // --- Shared Modifiers (Outer Tab) ---
  const handleShowAddModGroup = () => {
    setModalMode("add");
    setModGroupFormData({ id: null, name: "", is_required: false, max_choices: 1, product_id: null, is_active: true });
    setShowModGroupModal(true);
    setError("");
  };

  const handleShowEditModGroup = (group) => {
    setModalMode("edit");
    setModGroupFormData({ id: group.id, name: group.name, is_required: Boolean(group.is_required), max_choices: group.max_choices, product_id: group.product_id, is_active: group.is_active === 1 || group.is_active === true });
    setShowModGroupModal(true);
    setError("");
  };

  const handleModGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === "add") await createModifierGroupApi(token, modGroupFormData);
      else await updateModifierGroupApi(token, modGroupFormData.id, modGroupFormData);
      setShowModGroupModal(false);
      fetchSharedModifierGroups();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const confirmDelete = (type, id, name, parentId = null, index = null) => {
    setDeleteTarget({ type, id, name, parentId, index });
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget.type === "item") {
        await deleteProductApi(token, deleteTarget.id);
        fetchProducts();
      } else if (deleteTarget.type === "category") {
        await deleteProductCategoryApi(token, deleteTarget.id);
        fetchCategories();
      } else if (deleteTarget.type === "mod_group") {
        if (deleteTarget.id) {
          await deleteModifierGroupApi(token, deleteTarget.id);
          if (deleteTarget.parentId) await loadProductMappingAndSpecifics(deleteTarget.parentId);
          else fetchSharedModifierGroups();
        } else {
          // Add mode specific group
          const updated = [...productModGroups];
          updated.splice(deleteTarget.index, 1);
          setProductModGroups(updated);
        }
      } else if (deleteTarget.type === "mod_item") {
        if (deleteTarget.id) {
          await deleteModifierApi(token, deleteTarget.id);
          const res = await getModifiersByGroupApi(token, currentModGroup.id);
          setModItems(res.data);
        } else {
          // Add mode specific choice via modal (should only happen for shared now, but just in case)
          const updatedChoices = [...modItems];
          updatedChoices.splice(deleteTarget.index, 1);
          setModItems(updatedChoices);
          if (currentModGroupIndex !== null) {
            const updatedGroups = [...productModGroups];
            updatedGroups[currentModGroupIndex].choices = updatedChoices;
            setProductModGroups(updatedGroups);
          }
        }
      }
      setShowConfirmModal(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete");
      setShowConfirmModal(false);
    }
  };

  // --- Category / BOM Handlers ---
  const handleShowAddCat = () => {
    setModalMode("add");
    setCatFormData({ id: null, name: "" });
    setShowCatModal(true);
    setError("");
  };
  const handleShowEditCat = (cat) => {
    setModalMode("edit");
    setCatFormData({ id: cat.id, name: cat.name });
    setShowCatModal(true);
    setError("");
  };
  const handleCatSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === "add") await createProductCategoryApi(token, catFormData);
      else await updateProductCategoryApi(token, catFormData.id, catFormData);
      setShowCatModal(false);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };


  const handleShowModBom = async (modifier) => {
    setCurrentModifierForBom(modifier);
    if (modifier.id) {
      try {
        const res = await getModifierBomApi(token, modifier.id);
        setModBomItems(res.data);
        setShowModBomModal(true);
      } catch (err) {}
    } else {
      setModBomItems(modifier.bom || []);
      setShowModBomModal(true);
    }
  };
  const handleAddModBomItemRow = () => setModBomItems([...modBomItems, { inventory_item_id: "", quantity: 1, unit: "" }]);
  const handleRemoveModBomItemRow = (index) => {
    const newBom = [...modBomItems];
    newBom.splice(index, 1);
    setModBomItems(newBom);
  };
  const handleModBomItemChange = (index, field, value) => {
    const newBom = [...modBomItems];
    if (field === 'inventory_item_id') {
      const invItem = inventoryItems.find(i => String(i.id) === String(value));
      newBom[index].inventory_item_id = value;
      newBom[index].unit = invItem ? invItem.unit : "";
    } else {
      newBom[index][field] = value;
    }
    setModBomItems(newBom);
  };
  const handleModBomSubmit = async (e) => {
    e.preventDefault();
    try {
      const validBomItems = modBomItems.filter(item => item.inventory_item_id && item.quantity > 0)
        .map(item => ({ inventory_item_id: Number(item.inventory_item_id), quantity: Number(item.quantity) }));
      
      if (currentModifierForBom.id) {
        await updateModifierBomApi(token, currentModifierForBom.id, validBomItems);
      } else {
        if (currentModifierForBom.groupIndex !== undefined && currentModifierForBom.choiceIndex !== undefined) {
          const updatedGroups = [...productModGroups];
          updatedGroups[currentModifierForBom.groupIndex].choices[currentModifierForBom.choiceIndex].bom = validBomItems;
          setProductModGroups(updatedGroups);
        }
      }
      setShowModBomModal(false);
    } catch (err) {}
  };

  return (
    <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100%" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: "#1e3c72" }}><FaCoffee className="me-2" /> Menu & Modifiers</h2>
          <p className="text-muted mb-0 mt-1">Manage products, categories, and customization options</p>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2">
          {activeTab === "items" && (
            <>
              <Form.Control 
                type="text" 
                placeholder="Search Products..." 
                className="shadow-sm border-0 bg-white"
                style={{ width: '250px' }}
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Form.Select 
                className="shadow-sm border-0 bg-white"
                style={{ width: '200px' }}
                value={filterCategory} 
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
              {canEdit && <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAddItem}><FaPlus className="me-2"/> Add Product</Button>}
            </>
          )}

          {activeTab === "categories" && (
            <>
              <Form.Control 
                type="text" 
                placeholder="Search Categories..." 
                className="shadow-sm border-0 bg-white"
                style={{ width: '250px' }}
                value={searchCatQuery} 
                onChange={(e) => setSearchCatQuery(e.target.value)}
              />
              {canEdit && <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAddCat}><FaPlus className="me-2"/> Add Category</Button>}
            </>
          )}

          {activeTab === "modifiers" && (
            <>
              {canEdit && <Button className="px-4 shadow-sm ms-2 rounded text-white" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} onClick={handleShowAddModGroup}><FaPlus className="me-2"/> Add Group</Button>}
            </>
          )}
        </div>
      </div>

      <GlobalAlert message={error} onClose={() => setError("")} />

      <Card className="shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="px-3 pt-3 border-bottom-0">
        {/* TAB 1: MENU ITEMS */}
        <Tab eventKey="items" title={<><FaCoffee className="me-2"/>Menu Items</>}>
          
              <Table responsive hover className="mb-0 align-middle d-none d-md-table bg-white">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="ps-4">ID</th>
                    <th>Image</th>
                    <th>Menu Name</th>
                    <th>Category</th>
                    <th className="text-end">Branch Price (THB)</th>
                    <th className="text-center">Branch Status</th>
                    {canEdit && <th className="text-end pe-4">Actions</th>}
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loadingProducts ? (
                    <tr><td colSpan={6} className="text-center py-4">Loading...</td></tr>
                  ) : filteredProducts.map(product => (
                    <tr key={product.id}>
                      <td className="ps-4 fw-bold text-muted">#{product.id}</td>
                      <td>
                        {product.image_url ? (
                          <ImageViewerButton imageUrl={product.image_url} buttonText="ดูรูป" />
                        ) : (
                          <span className="text-muted small">ไม่มีรูป</span>
                        )}
                      </td>
                      <td className="fw-semibold">{product.name}</td>
                      <td>
                        {product.category_name ? <Badge bg="secondary" className="px-2 py-1 rounded-pill fw-normal">{product.category_name}</Badge> : <span className="text-muted fst-italic">None</span>}
                      </td>
                      <td className="px-4 py-3 text-end text-success fw-bold">฿{Number(product.price).toLocaleString()}</td>
                      <td className="text-center">
                        {(product.is_active === 1 || product.is_active === true) ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                      </td>
                      {canEdit && (
                        <td className="text-end pe-4 text-nowrap">
                          <Button variant="light" size="sm" className="me-1 text-primary border shadow-sm" onClick={() => handleShowBranchSettings(product)} title="Branch Settings"><FaLayerGroup /></Button>
                          <Button variant="light" size="sm" className="me-1 text-primary border shadow-sm" onClick={() => handleShowEditItem(product)} title="Edit Global Info"><FaEdit /></Button>
                          <Button variant="light" size="sm" className="text-danger border shadow-sm" onClick={() => confirmDelete('item', product.id, product.name)} title="Delete"><FaTrash /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            
        </Tab>
        
        {/* TAB 2: CATEGORIES */}
        <Tab eventKey="categories" title={<span><FaTags className="me-2"/>Categories</span>}>
          {/* Unchanged Categories Tab */}
          
              <Table responsive hover className="mb-0 align-middle bg-white">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="ps-4">ID</th>
                    <th>Category Name</th>
                    {canEdit && <th className="text-end pe-4">Actions</th>}
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {filteredCategories.map(cat => (
                    <tr key={cat.id}>
                      <td>#{cat.id}</td>
                      <td className="fw-semibold">{cat.name}</td>
                      {canEdit && (
                        <td className="text-end pe-4">
                          <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowEditCat(cat)}><FaEdit /></Button>
                          <Button variant="light" size="sm" className="text-danger" onClick={() => confirmDelete('category', cat.id, cat.name)}><FaTrash /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            
        </Tab>

        {/* TAB 3: SHARED MODIFIER GROUPS */}
        <Tab eventKey="modifiers" title={<span><FaLayerGroup className="me-2"/>Shared Modifiers</span>}>
          
              <Table responsive hover className="mb-0 align-middle bg-white">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="ps-4">Group Name</th>
                    <th className="text-center">Required</th>
                    <th className="text-center">Max Choices</th>
                    <th className="text-center">Status</th>
                    {canEdit && <th className="text-end pe-4">Actions</th>}
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loadingSharedModGroups ? (
                    <tr><td colSpan={4} className="text-center py-4">Loading...</td></tr>
                  ) : sharedModGroups.map(group => (
                    <tr key={group.id}>
                      <td className="fw-semibold">{group.name}</td>
                      <td className="text-center">{group.is_required ? <Badge bg="danger">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                      <td className="text-center">{group.max_choices}</td>
                      <td className="text-center">
                        {(group.is_active === 1 || group.is_active === true) ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                      </td>
                      {canEdit && (
                        <td className="text-end pe-4">
                          <Button variant="light" size="sm" className="me-2 text-success" onClick={() => handleManageChoices(group)}><FaListAlt /> Setup Choices</Button>
                          <Button variant="light" size="sm" className="me-2 text-primary" onClick={() => handleShowEditModGroup(group)}><FaEdit /></Button>
                          <Button variant="light" size="sm" className="text-danger" onClick={() => confirmDelete('mod_group', group.id, group.name)}><FaTrash /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            
        </Tab>
      </Tabs>
      </Card>

      {/* Branch Settings Modal */}
      <Modal show={showBranchModal} onHide={() => setShowBranchModal(false)} centered>
        <Form onSubmit={handleBranchSubmit}>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title style={{ color: "#1e3c72", fontWeight: 700 }}>Branch Settings for {branchFormData.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium text-muted">Branch Specific Price (Leave blank to use Global Base Price)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={branchFormData.custom_price || ""}
                onChange={(e) => setBranchFormData({ ...branchFormData, custom_price: e.target.value })}
                placeholder="Custom Price"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="branch-available-switch"
                label="Available in this branch"
                checked={branchFormData.is_available}
                onChange={(e) => setBranchFormData({ ...branchFormData, is_available: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="outline-secondary" onClick={() => setShowBranchModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" style={{ backgroundColor: "#1e3c72", borderColor: "#1e3c72" }} className="shadow-sm">Save Branch Settings</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ItemModal
        show={showItemModal}
        onHide={() => setShowItemModal(false)}
        modalMode={modalMode}
        itemFormData={itemFormData} setItemFormData={setItemFormData}
        categories={categories}
        sharedModGroups={sharedModGroups}
        selectedSharedGroups={selectedSharedGroups} setSelectedSharedGroups={setSelectedSharedGroups}
        productModGroups={productModGroups} setProductModGroups={setProductModGroups}
        bomItems={bomItems} setBomItems={setBomItems}
        inventoryItems={inventoryItems}
        handleShowModBom={handleShowModBom}
        handleItemSubmit={handleItemSubmit}
        productImageFile={productImageFile} setProductImageFile={setProductImageFile}
        productImagePreview={productImagePreview} setProductImagePreview={setProductImagePreview}
        error={error} setError={setError}
      />

      <ModItemsModal
        show={showModItemsModal}
        onHide={() => setShowModItemsModal(false)}
        currentModGroup={currentModGroup}
        modItems={modItems}
        handleAddChoiceRow={handleAddChoiceRow}
        handleModItemChange={handleModItemChange}
        handleAutoSaveChoice={handleAutoSaveChoice}
        handleShowModBom={handleShowModBom}
        confirmDelete={confirmDelete}
      />

      <ModGroupModal
        show={showModGroupModal}
        onHide={() => setShowModGroupModal(false)}
        modalMode={modalMode}
        modGroupFormData={modGroupFormData}
        setModGroupFormData={setModGroupFormData}
        handleModGroupSubmit={handleModGroupSubmit}
        error={error}
        setError={setError}
      />

      <CategoryModal
        show={showCatModal}
        onHide={() => setShowCatModal(false)}
        modalMode={modalMode}
        catFormData={catFormData}
        setCatFormData={setCatFormData}
        handleCatSubmit={handleCatSubmit}
        error={error}
        setError={setError}
      />

      {/* Bom Modal remains for old references if any, but replaced by ProductRecipeTab inside ItemModal */}

      <ModBomModal
        show={showModBomModal}
        onHide={() => setShowModBomModal(false)}
        currentModifierForBom={currentModifierForBom}
        modBomItems={modBomItems}
        inventoryItems={inventoryItems}
        handleAddModBomItemRow={handleAddModBomItemRow}
        handleModBomItemChange={handleModBomItemChange}
        handleRemoveModBomItemRow={handleRemoveModBomItemRow}
        handleModBomSubmit={handleModBomSubmit}
      />

      <ConfirmDeleteModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        deleteTarget={deleteTarget}
        handleDelete={handleDelete}
        error={error}
        setError={setError}
      />
    </Container>
  );
}
